/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Location,
  LocationWorkflow,
  CreateLocationPayload,
  UpdateLocationPayload,
  UpdateLocationWorkflowPayload,
} from '../../types/locationTypes';

export interface LocationsState {
  locations: Location[];
  globalLocation: Location | null;
  selectedLocation: Location | null;
  globalLocationWorkflow: LocationWorkflow | null;
  selectedLocationWorkflow: LocationWorkflow | null;
  fetchLocationsLoading: boolean;
  describeLocationLoading: boolean;
  describeLocationWorkflowLoading: boolean;
  createLocationLoading: boolean;
  updateLocationLoading: boolean;
  deleteLocationLoading: boolean;
  updateLocationWorkflowLoading: boolean;
  error: string | null;
}

const initialState: LocationsState = {
  locations: [],
  globalLocation: null,
  selectedLocation: null,
  globalLocationWorkflow: null,
  selectedLocationWorkflow: null,
  fetchLocationsLoading: false,
  describeLocationLoading: false,
  describeLocationWorkflowLoading: false,
  createLocationLoading: false,
  updateLocationLoading: false,
  deleteLocationLoading: false,
  updateLocationWorkflowLoading: false,
  error: null,
};

type LocationsLoadingKey =
  | 'fetchLocationsLoading'
  | 'describeLocationLoading'
  | 'describeLocationWorkflowLoading'
  | 'createLocationLoading'
  | 'updateLocationLoading'
  | 'deleteLocationLoading'
  | 'updateLocationWorkflowLoading';

const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    fetchLocations(
      state,
      _action: PayloadAction<{ orgId: number; status?: number }>,
    ) {
      state.fetchLocationsLoading = true;
    },
    describeLocation(
      state,
      _action: PayloadAction<{
        orgId: number;
        locationId: number;
        global?: boolean;
      }>,
    ) {
      state.describeLocationLoading = true;
    },
    createLocation(
      state,
      _action: PayloadAction<{
        orgId: number;
        location: Partial<CreateLocationPayload>;
      }>,
    ) {
      state.createLocationLoading = true;
    },
    updateLocation(
      state,
      _action: PayloadAction<{
        orgId: number;
        locationId: number;
        location: Partial<UpdateLocationPayload>;
      }>,
    ) {
      state.updateLocationLoading = true;
    },
    deleteLocation(
      state,
      _action: PayloadAction<{ orgId: number; locationId: number }>,
    ) {
      state.deleteLocationLoading = true;
    },
    describeLocationWorkflow(
      state,
      _action: PayloadAction<{
        orgId: number;
        locationId: number;
        global?: boolean;
      }>,
    ) {
      state.describeLocationWorkflowLoading = true;
    },
    updateLocationWorkflow(
      state,
      _action: PayloadAction<{
        orgId: number;
        locationId: number;
        workflow: Partial<UpdateLocationWorkflowPayload>;
      }>,
    ) {
      state.updateLocationWorkflowLoading = true;
    },

    setLocations(state, action: PayloadAction<Location[]>) {
      state.locations = action.payload;
      state.fetchLocationsLoading = false;
    },
    setSelectedLocation(
      state,
      action: PayloadAction<{ data: Location; global?: boolean }>,
    ) {
      if (action.payload.global) state.globalLocation = action.payload.data;
      else state.selectedLocation = action.payload.data;
      state.describeLocationLoading = false;
    },
    setSelectedLocationWorkflow(
      state,
      action: PayloadAction<{ data: LocationWorkflow; global?: boolean }>,
    ) {
      if (action.payload.global) {
        state.globalLocationWorkflow = action.payload.data;
      } else state.selectedLocationWorkflow = action.payload.data;
      state.describeLocationWorkflowLoading = false;
    },

    setLoading(
      state,
      action: PayloadAction<{ key: LocationsLoadingKey; value: boolean }>,
    ) {
      state[action.payload.key] = action.payload.value;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.fetchLocationsLoading = false;
      state.describeLocationLoading = false;
      state.describeLocationWorkflowLoading = false;
    },

    clearLocations(state) {
      state.locations = initialState.locations;
    },
    clearSelectedLocation(state) {
      state.selectedLocation = initialState.selectedLocation;
      state.selectedLocationWorkflow = initialState.selectedLocationWorkflow;
    },
    clearLocationsState() {
      return initialState;
    },
    setLocation(
      state,
      action: PayloadAction<{
        type: 'global' | 'selected';
        location: Location | null;
      }>,
    ) {
      if (action.payload.type === 'global') {
        state.globalLocation = action.payload.location;
      } else state.selectedLocation = action.payload.location;
    },
    setLocationWorkflow(
      state,
      action: PayloadAction<{
        type: 'global' | 'selected';
        workflow: LocationWorkflow | null;
      }>,
    ) {
      if (action.payload.type === 'global') {
        state.globalLocationWorkflow = action.payload.workflow;
      } else state.selectedLocationWorkflow = action.payload.workflow;
    },
  },
});

export const {
  fetchLocations,
  describeLocation,
  createLocation,
  updateLocation,
  deleteLocation,
  describeLocationWorkflow,
  updateLocationWorkflow,
  clearLocations,
  clearSelectedLocation,
  clearLocationsState,
  setLocation,
  setLocationWorkflow,
} = locationsSlice.actions;

export const locationsActions = locationsSlice.actions;
export default locationsSlice.reducer;
